import { useState } from 'react';
import type { OrderLineDto } from '../../../../../../../generated-api';
import { useMenuCourses } from '../../../../../../context/MenuSectionsStore';
import { Button } from '@ordr-as/umami-react/button';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { Radio, RadioGroup } from '@ordr-as/umami-react/radio';
import AddIcon from '@ordr-as/icons/AddIcon';
import SubtractIcon from '@ordr-as/icons/SubtractIcon';
import { useCartStore } from '../../../../../../context/cart/CartStore';
import type { AddItemsDto, DeleteItemsDto, EditItemWrapper } from '../../../../../../../types/cart';

interface ChangeCourseDialog {
  onClose: () => void;
  orderLine: OrderLineDto;
}

function ChangeCourseDialog({ ...props }: ChangeCourseDialog) {
  const { onClose, orderLine } = props;
  const moveCourse = useCartStore((x) => x.moveCourse);
  const menuCourses = useMenuCourses();
  const [quantityToMove, setQuantityToMove] = useState<number>(orderLine.quantity);

  const updateCourse = (courseId: string | undefined) => {
    /**
     * OrderLine: the current item BEFORE editing
     * item: The new edited item
     * quantity: The new quantity for the whole item and orderLine.quantity is the old quantity
     */

    const orginalItemToDelete: DeleteItemsDto = {
      item: {
        comment: orderLine.comment,
        courseId: orderLine.courseId,
        giftCardId: orderLine.giftCardId,
        itemVariantId: orderLine.itemVariantId,
        menuSectionId: orderLine.menuSectionId,
        name: orderLine.name,
        openPrice: orderLine.openPrice,
        options: orderLine.options,
        price: orderLine.price,
        sectionProductId: orderLine.sectionProductId,
        takeawayPrice: orderLine.takeawayPrice,
      },
      quantity: quantityToMove,
    };

    const changedItemToAdd: AddItemsDto = {
      item: {
        comment: orderLine.comment,
        courseId,
        giftCardId: orderLine.giftCardId,
        itemVariantId: orderLine.itemVariantId,
        menuSectionId: orderLine.menuSectionId,
        name: orderLine.name,
        openPrice: orderLine.openPrice,
        options: orderLine.options,
        price: orderLine.price,
        sectionProductId: orderLine.sectionProductId,
        takeawayPrice: orderLine.takeawayPrice,
      },
      quantity: quantityToMove,
    };

    const changeRequest: EditItemWrapper = {
      addItemsDto: changedItemToAdd,
      deleteItemsDto: orginalItemToDelete,
    };

    moveCourse(changeRequest);
    onClose();
  };

  const handleCourseChange = (value: string) => {
    updateCourse(value === 'No course' ? undefined : value);
  };

  return (
    <Dialog className="w-full mx-4 tablet:w-1/2 tablet:mx-0" onClose={onClose} title="Change course" alignTop>
      <RadioGroup
        className="flex flex-col gap-4"
        defaultValue={orderLine.courseId}
        aria-label="Change course"
        onChange={(value) => handleCourseChange(value)}
      >
        <div className="flex flex-row justify-between items-center">
          <p>Quantity to move </p>
          <div className="flex flex-row gap-2 items-center">
            <Button
              kind="secondary"
              isDisabled={quantityToMove === 0}
              className="flex-shrink-0 rounded-full h-"
              onClick={() => setQuantityToMove(quantityToMove - 1)}
            >
              <SubtractIcon className="h-4" />
            </Button>
            <p>
              {quantityToMove} of {orderLine.quantity}
            </p>
            <Button
              kind="secondary"
              isDisabled={quantityToMove === orderLine.quantity}
              className="flex-shrink-0 rounded-full"
              onClick={() => setQuantityToMove(quantityToMove + 1)}
            >
              <AddIcon className="h-4" />
            </Button>
          </div>
        </div>
        <>
          {menuCourses.map((course) => (
            <Radio
              className="h-7"
              // IsDisabled={editItemsInCartMutation.isPending}
              key={course.courseId}
              aria-label={course.name}
              value={course.courseId}
            >
              {course.name}
            </Radio>
          ))}
        </>
      </RadioGroup>
    </Dialog>
  );
}

export default ChangeCourseDialog;
